/**
 * Milhar 的 API 桥接。
 *
 * 形状以 `doc/MILHAR_ENGINEERING_CONTRACT.md §1` 为准 —— **字段名不得单方更改**。
 */

import { useAuthStore } from '../auth/authStore';
import { apiFetch, authHeader } from '../../shared/api/httpClient';
import type { MilharTierId } from '../../shared/milhar/tiers';

const jsonHeaders = { 'Content-Type': 'application/json' };

function decodeBase64Url(value: string): ArrayBuffer {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return bytes.buffer as ArrayBuffer;
}

function hex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

async function signedHeaders(path: string, rawBody: string): Promise<Record<string, string>> {
  // The login response stores this Base64URL-encoded 32-byte secret.
  const secret = useAuthStore.getState().sessionSecret;
  if (!secret) return {};
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const nonce = crypto.randomUUID();
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(rawBody));
  const canonical = `${nonce}|${timestamp}|POST|${path}|${hex(digest)}`;
  const key = await crypto.subtle.importKey(
    'raw', decodeBase64Url(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(canonical));
  return {
    'X-Sorteia-Timestamp': timestamp,
    'X-Sorteia-Nonce': nonce,
    'X-Sorteia-Signature': hex(signature),
  };
}

async function postMilhar<T>(path: string, token: string, body: object): Promise<T> {
  const rawBody = JSON.stringify(body);
  const securityHeaders = await signedHeaders(path, rawBody);
  const res = await fetch(path, {
    method: 'POST',
    headers: { ...jsonHeaders, ...securityHeaders, Authorization: `Bearer ${token}` },
    body: rawBody,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.code || 'UNKNOWN');
  return data as T;
}

export type MilharPhase = 'OPEN' | 'WAITING';

export type MilharStatus = {
  ok: boolean;
  enabled: boolean;
  bettingEnabled: boolean;
  available: boolean;
  tier?: MilharTierId;
  pool: {
    currency: string;
    /** 展示给用户的数字 = 池子 × 80%。**绝不展示 poolTotal**（master §4.1.1）。 */
    takeHome: string;
    poolTotal: string;
    state: 'NORMAL' | 'MADURO';
    roundsAccumulating: number;
    winningDigits: number;
  };
  config: {
    ticketPrice: string;
    digits: number;
    multipliers: Record<string, string>;
    teimosinhaOptions: number[];
    /**
     * 期数上限，同时是**后端能力位**。
     *
     * <p>可缺席：字段在，说明后端已放开成任意 1..maxRounds，期数步进器才按 +1 走；
     * 字段不在（前端先于后端部署），退回 `teimosinhaOptions` 的档位步进 —— 否则
     * 用户能选出一个后端仍拒收的期数，直到付款那一刻才报 MILHAR_INVALID_ROUNDS。
     */
    maxRounds?: number;
    maxQuantity: number;
    /** 留作下期种子的百分比（已 ×100，如 "20"）。规则页用，前端不写死。 */
    seedRetainPct: string;
    /** 每注进奖池的百分比（已 ×100，如 "25"）。**前端不许写死**——见 injectionFromAmount。 */
    jackpotContribPct?: string;
    /**
     * 总返还率，已 ×100（如 `"88"`）。后端从当前生效的倍数表算出，不是常量。
     *
     * <p>可缺席（老后端没有，且玩法未启用时刻意不发）。缺席时**整段投注分流图
     * 不渲染** —— 不得退回「25% / 75%」两分图，那正是这次要消灭的
     * 「平台拿走 75%」误读。
     */
    rtpPct?: string;
    /**
     * 本档读 `D1..D10` 的哪一段，1-based 闭区间 `[start, end]`。
     *
     * <p>可缺席（老后端没有）。规则页据此高亮窗口 —— 兜底见
     * `shared/milhar/rulesWindow.ts` 的 FALLBACK_WINDOW。
     */
    digitWindow?: number[];
    /** MADURO 时降到几位就能中头奖。可缺席。规则页用，前端不写死。 */
    maduroDigits?: number;
  };
  /**
   * 服务端此刻的时刻，秒。前端据此校正设备时钟 —— 见 `shared/lib/clockSkew.ts`。
   * 可缺席：后端旧版本没有这个字段，此时不校正（行为与从前一致）。
   */
  serverTime?: number;
  round: { number: number; label: string; endsAt: number; phase: MilharPhase };
  /**
   * 上一期还在开奖时才出现 —— 这一期的下单被闸住了。
   *
   * <p>规则在后端（`PREV_ROUND_DRAWING`，与房间玩法同一条）。前端拿 `endsAt` 在本地
   * 跑区块动画（轮询 5 秒一次，扛不住 12 秒一格），但**何时放行以服务端为准**：
   * 这个字段消失，闸门就结束。
   */
  drawing?: { round: number; endsAt: number; graceSec: number };
  myBets: MilharBet[];
  /** `mine` / `payout` 只在登录时有意义；访客拿到的是 false / null。 */
  recentResults: {
    round: number; label: string; result: string;
    mine?: boolean; payout?: string | null;
    /**
     * 这一期开奖窗口的起点（`rounds.cutoff_ts`）。
     *
     * **可缺席** —— 后端早于这个字段的版本不会带它，那时结果带只显示期号，不显示
     * 时刻，也不插跨天分隔。不要兜底成 0：0 会被渲染成 1970。
     */
    cutoffTs?: number;
  }[];
  /**
   * 本期买家流。定稿 `doc/design/MILHAR_ATMOSPHERE_SPEC.md` §2。
   *
   * <p>**可缺席** —— 后端还没实现时整块社交区不渲染（§3.1），前端因此可以
   * 先于后端上线。不要给它兜底成空对象：`{people:0}` 与「后端没给」在
   * `hasFeed` 里是同一个结论，但兜底会让「接口坏了」看起来像「本期没人」。
   */
  roundFeed?: MilharRoundFeed;
  /** §15.1 —— 近期头奖得主。**必须带号码**：号码是「这真的会发生」的证据。 */
  jackpotWinners?: {
    round: number; label: string; pick: string; payout: string;
    /**
     * handle。**可为 `null`** —— 这个人关掉了公开榜 / 被封禁或自我排除 / 已注销
     * （后端 `MilharStatsService.JACKPOT_WINNERS_SQL` 的 `showable`）。派彩这一行
     * 仍然下发，只是身份不下发；`userId` / `avatarDefaultId` / `avatarThumbUrl`
     * 会跟着一起是 `null`。前端渲染成匿名行，人名按钮 disabled。
     */
    username: string | null;
    /**
     * 打开公开资料弹层要它。`null` = 身份被隐藏（见上）；`undefined` = 老后端
     * 还没实现这个字段。两种情况下该行的人名都不可点，前端不需要区分。
     */
    userId?: number | null;
    /** 默认头像包编号；没选过、或身份被隐藏时为 null。可缺席（老后端）。 */
    avatarDefaultId?: number | null;
    /** 自定义头像；没传过、或身份被隐藏时为 null。可缺席（老后端）。 */
    avatarThumbUrl?: string | null;
    /**
     * 该期开奖时刻，秒。可缺席（老后端），此时**整个时间片段省略** ——
     * 不要渲染 `localListTime` 的 `'--'` 兜底，那在这一行里是个看起来
     * 像坏了的占位符，而这一行少一个时间仍然完整。
     */
    cutoffTs?: number;
  }[];
};

/** 见 `shared/milhar/roundFeedView.ts` —— 那里有播放与注入的口径。 */
export type MilharRoundFeed = {
  orders: {
    id: number;
    userId: number;
    username: string;
    avatarDefaultId?: number | null;
    avatarThumbUrl?: string | null;
    vipLevel?: number | null;
    pick: string;
    /** money() 序列化出来的字符串，如 "5.00"。**不是 number**。 */
    amount: string;
    placedAt: number;
    teimosinha: boolean;
  }[];
  people: number;
  tickets: number;
  injected: string;
};

export type MilharBet = {
  id: number;
  round: number;
  roundLabel: string;
  /** Authoritative draw result for this bet's round; null before draw or after VOID. */
  result: string | null;
  pick: string;
  amount: string;
  /** 实际派奖额。**前端不自算奖金** —— 头奖按注额平分，推不出来。 */
  payout: string | null;
  status: string;
  /** 结算前为 null。**前端不自算输赢**，reveal 的逐位高亮读它。 */
  matched: number | null;
  subscriptionId: number | null;
  roundsLeft: number | null;
};

export type MilharLedger = {
  ok: boolean;
  totals: {
    in: string; out: string; seed: string; adjust: string; pot: string; balanced: boolean;
    /** 本轮种子来自哪一期的开奖；平台开办资金时为 null。 */
    seedFromRound?: number | null;
  };
  entries: {
    round: number;
    /**
     * `CONTRIB` / `SEED` / `PAYOUT` / `ADJUST`，或 **`NONE`** —— 这一期开过奖但
     * 没人投注，后端合成的 0 行：`amount` 为 "0.00"，`chainHash` / `createdAt` 为 null，
     * `balanceAfter` 是它之前最近一笔的余额（池子没动）。
     */
    kind: string;
    amount: string;
    balanceAfter: string; chainHash: string | null; createdAt: number | null;
    /**
     * 该期的开奖时刻，秒。**与 `createdAt` 不是一回事** —— 后者是结算写账的时刻。
     * 可缺席（老后端没有），此时行内退回 `createdAt` 的日期。
     */
    cutoffTs?: number | null;
    /**
     * 日内序号，**自带 `#` 前缀**（如 `"#093"`，见 `MilharTier.roundLabel`）。
     * 用它时不要再补一个 `#`。可缺席，此时退回全局号。
     */
    roundLabel?: string | null;
  }[];
  nextCursor: string | null;
};

async function getJson<T>(url: string, token?: string | null): Promise<T> {
  return apiFetch<T>(url, { headers: authHeader(token) });
}

/** 未登录也能读 —— 奖池和最近结果是信任与营销素材（工程契约 §7.1）。 */
export function fetchMilharStatus(token?: string | null, tier: MilharTierId = '4') {
  return getJson<MilharStatus>(`/api/v1/milhar/status?tier=${tier}`, token);
}

/** @returns 已有多少**其他**人选了这个号；0 时前端整行不渲染 */
export async function fetchPickCount(
  pick: string, token?: string | null, tier: MilharTierId = '4',
): Promise<number> {
  try {
    const data = await getJson<{ people: number }>(
      `/api/v1/milhar/picks/${encodeURIComponent(pick)}?tier=${tier}`, token);
    return data.people ?? 0;
  } catch {
    // 拿不到就当没有 —— 视觉规格 §8.5.3：整行不渲染，不要显示错误
    return 0;
  }
}

export function fetchRoundPicks(token?: string | null, tier: MilharTierId = '4') {
  return getJson<{ picks: { pick: string; people: number }[] }>(
    `/api/v1/milhar/rounds/current/picks?tier=${tier}`, token);
}

/**
 * 奖池账本，**按期分页**：每一期至少一行，没人投注的期是一行 `kind: 'NONE'`、
 * 金额 0（2026-09-14 PM：「即使这轮没有人投注，也要标记出这一轮是 0」）。
 * `cursor` 是上一页 `nextCursor`（期号），首页不传。
 */
export function fetchMilharLedger(
  token?: string | null, tier: MilharTierId = '4', cursor?: string | null,
) {
  const suffix = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
  return getJson<MilharLedger>(`/api/v1/milhar/ledger?tier=${tier}${suffix}`, token);
}

export async function placeMilharBet(
  token: string,
  body: { pick: string; amount: string; idempotencyKey: string; tier?: MilharTierId },
) {
  return postMilhar<{ betId: number; round: number; pick: string }>(
    '/api/v1/milhar/bets', token, body);
}

export async function createTeimosinha(
  token: string,
  body: {
    pick: string; stake: string; quantity: number;
    rounds: number; idempotencyKey: string; tier?: MilharTierId;
  },
) {
  return postMilhar<{ subscriptionId: number; prepaidAmount: string; firstRound: number }>(
    '/api/v1/milhar/subscriptions', token, body);
}

export type MilharStats = {
  ok: boolean;
  windows: number[];
  window: number;
  /** 实际样本量，可能小于 window（新环境开奖期数还不够）。**分母用它，不用 window**。 */
  sample: number;
  digits: number;
  expected: number;
  lowerBound: number;
  upperBound: number;
  positions: { position: number; counts: number[]; gaps: number[] }[];
  /**
   * 每期一行。`drawnAt` 与 `/rounds/{round}` 的同名字段是同一列（`rounds.cutoff_ts`），
   * 两个页面不会对同一期显示两个时间 —— 账本的 `createdAt` 实测比开奖晚约 340 秒，
   * 在 5 分钟档上落进了下一期的窗口，不能拿来当开奖时间。
   *
   * `staked` 是字符串金额（仓库惯例）。
   */
  history: {
    round: number; label: string; result: string;
    drawnAt: number; staked: string;
    /** 这一期有人拿走了累积奖池。判据是账本的 PAYOUT，不是「有人赢钱」。 */
    jackpot: boolean;
    /**
     * 「这一期有没有我」。**三个字段全部可缺席** —— 前端先于后端上线时整列
     * 竖条不渲染，而不是渲染一排「没参与」。未登录时后端 `mine` 恒 false。
     */
    mine?: boolean;
    myStaked?: string;
    myPayout?: string | null;
  }[];
};

export type MilharHistoryItem = MilharStats['history'][number];

export type MilharMineStats = {
  ok: boolean;
  /** 与 `MilharStats.history` 逐字同构 —— 两个页签共用同一个行组件。 */
  history: MilharHistoryItem[];
  /** 只有第一页带，后续页为 null。描述的是全部历史，不是已加载的这几页。 */
  summary?: { rounds?: number; won?: number; staked_total?: string; payout_total?: string } | null;
  /** null 表示没有更早的了。游标是期号。 */
  next_cursor?: string | null;
};

/**
 * 我下过注的那些轮次。
 *
 * <p>`/stats` 返回的是「最近 N 期」（N 最大 1000），参与与否只是每一行的一个
 * 装饰；这个接口反过来 —— 只返回我真的下过注的期，按期号往前翻，不受那 1000
 * 期的窗口限制。玩家要找的是「我 8 月那次中的是哪一期」，在一千行里逐行找
 * 竖条不是一个能完成的任务（2026-09-17 PM）。
 */
export function fetchMilharMineStats(
  token: string | null, cursor?: string | null, tier: MilharTierId = '4', limit = 50,
) {
  const params = new URLSearchParams({ tier, limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return getJson<MilharMineStats>(`/api/v1/milhar/stats/mine?${params.toString()}`, token);
}

export function fetchMilharStats(
  window: number, token?: string | null, tier: MilharTierId = '4',
) {
  return getJson<MilharStats>(`/api/v1/milhar/stats?window=${window}&tier=${tier}`, token);
}

/** 某一期的完整账单。视觉规格 §12B.1 + 2026-08-19 评审。 */
export type MilharRoundDetail = {
  ok: boolean;
  round: number;
  label: string;
  drawnAt: number;
  /** 未开奖时为 null —— 别拿空串冒充结果 */
  result: string | null;
  ledger: {
    kind: string; amount: string; balanceAfter: string;
    chainHash: string; createdAt: number;
  } | null;
  totals: { people: number; tickets: number; staked: string; paid: string };
  bets: {
    id: number; userId: number; username: string;
    vipLevel?: number | null; avatarDefaultId?: number | null; avatarThumbUrl?: string | null;
    pick: string; amount: string; matched: number | null; payout: string;
  }[];
};

/** 未登录也能读 —— 与买家名单同一个公开决定。 */
export function fetchMilharRoundDetail(
  round: number, token?: string | null, tier: MilharTierId = '4',
) {
  return getJson<MilharRoundDetail>(
    `/api/v1/milhar/rounds/${round}?tier=${tier}`, token);
}
